import {
  query,
  where,
  getDocs,
  collectionGroup,
  orderBy,
  limit,
  Timestamp,
  getCountFromServer,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import type { Subscription } from '@bevpass/types';

export interface SubscriptionStats {
  activeCount: number;
  totalRevenue: number; // in cents
  averageRevenue: number; // in cents
  churnRate: number;
  trialCount: number;
  pastDueCount: number;
}

export interface PlanSubscriptionStats {
  planId: string;
  activeSubscribers: number;
  monthlyRevenue: number; // in cents
  trialSubscribers: number;
  churnedThisMonth: number;
  averageSubscriptionValue: number; // in cents
}

class SubscriptionsService {
  private normalizeSubscription(raw: Record<string, unknown>, docId?: string): Subscription {
    const stripePriceDetails = raw.stripePriceDetails as Record<string, unknown> | undefined;

    const amount = typeof raw.amount === 'number'
      ? raw.amount
      : typeof stripePriceDetails?.amount === 'number'
        ? stripePriceDetails.amount
        : Number(raw.amount ?? stripePriceDetails?.amount ?? 0);

    const currency = typeof raw.currency === 'string'
      ? raw.currency
      : typeof stripePriceDetails?.currency === 'string'
        ? stripePriceDetails.currency
        : 'usd';

    const interval = typeof raw.interval === 'string'
      ? raw.interval
      : typeof stripePriceDetails?.interval === 'string'
        ? stripePriceDetails.interval
        : 'month';

    const intervalCount = typeof raw.intervalCount === 'number'
      ? raw.intervalCount
      : typeof stripePriceDetails?.intervalCount === 'number'
        ? stripePriceDetails.intervalCount
        : 1;

    const subscriptionId = typeof raw.subscriptionId === 'string'
      ? raw.subscriptionId
      : docId ?? '';

    const coerceDate = (value: unknown): Date => {
      if (!value && value !== 0) return new Date(0);
      if (value instanceof Timestamp) return value.toDate();
      if (value instanceof Date) return value;
      if (typeof value === 'number') return new Date(value * 1000);
      return new Date(value as string);
    };

    return {
      ...(raw as Subscription),
      subscriptionId,
      amount,
      currency,
      interval,
      intervalCount,
      createdAt: coerceDate(raw.createdAt),
      updatedAt: coerceDate(raw.updatedAt),
      currentPeriodStart: coerceDate(raw.currentPeriodStart),
      currentPeriodEnd: coerceDate(raw.currentPeriodEnd),
    } as Subscription;
  }

  private computeMonthlyAmountCents(sub: { amount: number; interval: string; intervalCount: number }): number {
    const count = Math.max(1, sub.intervalCount || 1);
    if (sub.interval === 'year') return Math.round(sub.amount / (12 * count));
    if (sub.interval === 'month') return Math.round(sub.amount / count);
    if (sub.interval === 'week') return Math.round((sub.amount * 4.333) / count);
    if (sub.interval === 'day') return Math.round((sub.amount * 30) / count);
    return sub.amount;
  }

  private async fetchSubscribersViaFunction(
    merchantId: string
  ): Promise<Record<string, unknown>[] | null> {
    try {
      const callable = httpsCallable<{ merchantId: string }, { subscribers?: unknown[] }>(
        functions,
        'getMerchantSubscribersRaw'
      );
      const res = await callable({ merchantId });
      const subscribers = res.data?.subscribers;
      return Array.isArray(subscribers)
        ? (subscribers as Record<string, unknown>[])
        : [];
    } catch (err) {
      console.error('Callable getMerchantSubscribersRaw failed:', err);
      return null;
    }
  }
  
  // Get all active subscriptions for a merchant
  async getActiveSubscriptionsByMerchant(merchantId: string): Promise<Subscription[]> {
    try {
      const subscriptionsQuery = query(
        collectionGroup(db, 'subscriptions'),
        where('merchantId', '==', merchantId),
        where('status', '==', 'active')
      );
      
      const snapshot = await getDocs(subscriptionsQuery);
      return snapshot.docs.map(doc => this.normalizeSubscription(doc.data(), doc.id));
    } catch (error) {
      console.error('Error fetching active subscriptions:', error);
      return [];
    }
  }

  // Get subscription stats for a specific plan
  async getSubscriptionStatsForPlan(
    merchantId: string,
    planId: string
  ): Promise<PlanSubscriptionStats> {
    try {
      // Prefer callable to avoid client permissions/index issues
      const subsFromFn = await this.fetchSubscribersViaFunction(merchantId);
      if (subsFromFn !== null) {
        const normalized = subsFromFn
          .filter((s) => typeof s.planId === 'string' && s.planId === planId)
          .map((s) =>
            this.normalizeSubscription(
              s,
              typeof s.subscriptionId === 'string' ? s.subscriptionId : undefined
            )
          );

        const activeSubscribers = normalized.filter((sub) => sub.status === 'active').length;
        const trialSubscribers = normalized.filter((sub) => sub.status === 'trialing').length;
        const monthlyRevenue = normalized
          .filter((sub) => sub.status === 'active')
          .reduce((total, sub) => total + this.computeMonthlyAmountCents(sub), 0);
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const churnedThisMonth = normalized.filter((sub) => sub.status === 'canceled' && sub.updatedAt > thirtyDaysAgo).length;
        const averageSubscriptionValue = activeSubscribers > 0 ? Math.round(monthlyRevenue / activeSubscribers) : 0;

        return { planId, activeSubscribers, monthlyRevenue, trialSubscribers, churnedThisMonth, averageSubscriptionValue };
      }

      // Query all subscriptions for this plan
      const subscriptionsQuery = query(
        collectionGroup(db, 'subscriptions'),
        where('merchantId', '==', merchantId),
        where('planId', '==', planId)
      );

      const snapshot = await getDocs(subscriptionsQuery);
      const subscriptions = snapshot.docs.map(doc => this.normalizeSubscription(doc.data(), doc.id));

      // Count active subscribers
      const activeSubscribers = subscriptions.filter(
        sub => sub.status === 'active'
      ).length;

      // Count trial subscribers
      const trialSubscribers = subscriptions.filter(
        sub => sub.status === 'trialing'
      ).length;

      // Calculate monthly revenue (MRR) from active subscriptions
      const monthlyRevenue = subscriptions
        .filter(sub => sub.status === 'active')
        .reduce((total, sub) => {
          // Convert to monthly equivalent
          const count = Math.max(1, sub.intervalCount || 1);
          let monthlyAmount = sub.amount;
          if (sub.interval === 'year') {
            monthlyAmount = Math.round(sub.amount / (12 * count));
          } else if (sub.interval === 'month') {
            monthlyAmount = Math.round(sub.amount / count);
          } else if (sub.interval === 'week') {
            monthlyAmount = Math.round((sub.amount * 4.333) / count); // approx weeks/month
          } else if (sub.interval === 'day') {
            monthlyAmount = Math.round((sub.amount * 30) / count);
          }
          return total + monthlyAmount;
        }, 0);

      // Calculate churned this month (subscriptions canceled in last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const churnedThisMonth = subscriptions.filter(sub => sub.status === 'canceled' && sub.updatedAt > thirtyDaysAgo).length;

      // Calculate average subscription value
      const averageSubscriptionValue = activeSubscribers > 0
        ? Math.round(monthlyRevenue / activeSubscribers)
        : 0;

      return {
        planId,
        activeSubscribers,
        monthlyRevenue,
        trialSubscribers,
        churnedThisMonth,
        averageSubscriptionValue,
      };
    } catch (error) {
      console.warn(`Error fetching subscription stats for plan ${planId}:`, error);
      // Fallback via callable function (admin env)
      try {
        const subs = await this.fetchSubscribersViaFunction(merchantId);
        if (!subs) {
          return {
            planId,
            activeSubscribers: 0,
            monthlyRevenue: 0,
            trialSubscribers: 0,
            churnedThisMonth: 0,
            averageSubscriptionValue: 0,
          };
        }

        const normalized = subs
          .filter((s) => s.planId === planId)
          .map((s) =>
            this.normalizeSubscription(
              {
                ...s,
                // ensure price fields present for math
                amount: s.amount ?? s.stripePriceDetails?.amount ?? 0,
                interval: s.interval ?? s.stripePriceDetails?.interval ?? 'month',
                intervalCount: s.intervalCount ?? s.stripePriceDetails?.intervalCount ?? 1,
              },
              s.subscriptionId
            )
          );

        const activeSubscribers = normalized.filter((sub) => sub.status === 'active').length;
        const trialSubscribers = normalized.filter((sub) => sub.status === 'trialing').length;
        const monthlyRevenue = normalized
          .filter((sub) => sub.status === 'active')
          .reduce((total, sub) => total + this.computeMonthlyAmountCents(sub), 0);

        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const churnedThisMonth = normalized.filter((sub) => sub.status === 'canceled' && sub.updatedAt > thirtyDaysAgo).length;

        const averageSubscriptionValue = activeSubscribers > 0 ? Math.round(monthlyRevenue / activeSubscribers) : 0;

        return {
          planId,
          activeSubscribers,
          monthlyRevenue,
          trialSubscribers,
          churnedThisMonth,
          averageSubscriptionValue,
        };
      } catch (fallbackErr) {
        console.warn('Fallback compute via function failed:', fallbackErr);
        return {
          planId,
          activeSubscribers: 0,
          monthlyRevenue: 0,
          trialSubscribers: 0,
          churnedThisMonth: 0,
          averageSubscriptionValue: 0,
        };
      }
    }
  }

  // Get subscription stats for multiple plans
  async getSubscriptionStatsForPlans(
    merchantId: string,
    planIds: string[]
  ): Promise<Map<string, PlanSubscriptionStats>> {
    const statsMap = new Map<string, PlanSubscriptionStats>();
    
    // Fetch stats for all plans in parallel
    const statsPromises = planIds.map(planId =>
      this.getSubscriptionStatsForPlan(merchantId, planId)
    );
    
    const allStats = await Promise.all(statsPromises);
    
    allStats.forEach(stats => {
      statsMap.set(stats.planId, stats);
    });
    
    return statsMap;
  }

  // Get overall subscription stats for a merchant
  async getOverallStats(merchantId: string): Promise<SubscriptionStats> {
    try {
      // Prefer callable to avoid client permissions/index issues
      const subsFromFn = await this.fetchSubscribersViaFunction(merchantId);
      if (subsFromFn !== null) {
        const normalized = subsFromFn.map((s) =>
          this.normalizeSubscription(
            s,
            typeof s.subscriptionId === 'string' ? s.subscriptionId : undefined
          )
        );
        const activeCount = normalized.filter((sub) => sub.status === 'active').length;
        const trialCount = normalized.filter((sub) => sub.status === 'trialing').length;
        const pastDueCount = normalized.filter((sub) => sub.status === 'past_due').length;
        const totalRevenue = normalized
          .filter((sub) => sub.status === 'active')
          .reduce((total, sub) => total + this.computeMonthlyAmountCents(sub), 0);
        const averageRevenue = activeCount > 0 ? Math.round(totalRevenue / activeCount) : 0;
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const recentlyCanceled = normalized.filter((sub) => sub.status === 'canceled' && sub.updatedAt > thirtyDaysAgo).length;
        const totalAtStartOfPeriod = activeCount + recentlyCanceled;
        const churnRate = totalAtStartOfPeriod > 0 ? (recentlyCanceled / totalAtStartOfPeriod) * 100 : 0;
        return { activeCount, totalRevenue, averageRevenue, churnRate, trialCount, pastDueCount };
      }

      // Query all subscriptions for this merchant
      const subscriptionsQuery = query(
        collectionGroup(db, 'subscriptions'),
        where('merchantId', '==', merchantId)
      );

      const snapshot = await getDocs(subscriptionsQuery);
      const subscriptions = snapshot.docs.map(doc => this.normalizeSubscription(doc.data(), doc.id));

      // Count by status
      const activeCount = subscriptions.filter(sub => sub.status === 'active').length;
      const trialCount = subscriptions.filter(sub => sub.status === 'trialing').length;
      const pastDueCount = subscriptions.filter(sub => sub.status === 'past_due').length;

      // Calculate total monthly revenue (MRR)
      const totalRevenue = subscriptions
        .filter(sub => sub.status === 'active')
        .reduce((total, sub) => {
          const count = Math.max(1, sub.intervalCount || 1);
          let monthlyAmount = sub.amount;
          if (sub.interval === 'year') {
            monthlyAmount = Math.round(sub.amount / (12 * count));
          } else if (sub.interval === 'month') {
            monthlyAmount = Math.round(sub.amount / count);
          } else if (sub.interval === 'week') {
            monthlyAmount = Math.round((sub.amount * 4.333) / count);
          } else if (sub.interval === 'day') {
            monthlyAmount = Math.round((sub.amount * 30) / count);
          }
          return total + monthlyAmount;
        }, 0);

      // Calculate average revenue per subscription
      const averageRevenue = activeCount > 0 
        ? Math.round(totalRevenue / activeCount)
        : 0;

      // Calculate churn rate (canceled in last 30 days / total at start of period)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const recentlyCanceled = subscriptions.filter(sub => sub.status === 'canceled' && sub.updatedAt > thirtyDaysAgo).length;

      const totalAtStartOfPeriod = activeCount + recentlyCanceled;
      const churnRate = totalAtStartOfPeriod > 0
        ? (recentlyCanceled / totalAtStartOfPeriod) * 100
        : 0;

      return {
        activeCount,
        totalRevenue,
        averageRevenue,
        churnRate,
        trialCount,
        pastDueCount,
      };
    } catch (error) {
      console.warn('Error fetching overall subscription stats:', error);
      // Fallback via callable function (admin env)
      try {
        const subs = await this.fetchSubscribersViaFunction(merchantId);
        if (!subs) {
          return {
            activeCount: 0,
            totalRevenue: 0,
            averageRevenue: 0,
            churnRate: 0,
            trialCount: 0,
            pastDueCount: 0,
          };
        }
        const normalized = subs.map((s) =>
          this.normalizeSubscription(
            s,
            typeof s.subscriptionId === 'string' ? s.subscriptionId : undefined
          )
        );

        const activeCount = normalized.filter((sub) => sub.status === 'active').length;
        const trialCount = normalized.filter((sub) => sub.status === 'trialing').length;
        const pastDueCount = normalized.filter((sub) => sub.status === 'past_due').length;
        const totalRevenue = normalized
          .filter((sub) => sub.status === 'active')
          .reduce((total, sub) => total + this.computeMonthlyAmountCents(sub), 0);
        const averageRevenue = activeCount > 0 ? Math.round(totalRevenue / activeCount) : 0;
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const recentlyCanceled = normalized.filter((sub) => sub.status === 'canceled' && sub.updatedAt > thirtyDaysAgo).length;
        const totalAtStartOfPeriod = activeCount + recentlyCanceled;
        const churnRate = totalAtStartOfPeriod > 0 ? (recentlyCanceled / totalAtStartOfPeriod) * 100 : 0;

        return { activeCount, totalRevenue, averageRevenue, churnRate, trialCount, pastDueCount };
      } catch (fallbackErr) {
        console.warn('Fallback overall stats via function failed:', fallbackErr);
        return {
          activeCount: 0,
          totalRevenue: 0,
          averageRevenue: 0,
          churnRate: 0,
          trialCount: 0,
          pastDueCount: 0,
        };
      }
    }
  }

  // Count active subscriptions using Firestore count query (more efficient)
  async countActiveSubscriptions(merchantId?: string): Promise<number> {
    try {
      let subscriptionsQuery;
      
      if (merchantId) {
        // Count for specific merchant
        subscriptionsQuery = query(
          collectionGroup(db, 'subscriptions'),
          where('merchantId', '==', merchantId),
          where('status', '==', 'active')
        );
      } else {
        // Count all active subscriptions across all merchants
        subscriptionsQuery = query(
          collectionGroup(db, 'subscriptions'),
          where('status', '==', 'active')
        );
      }
      
      const snapshot = await getCountFromServer(subscriptionsQuery);
      return snapshot.data().count;
    } catch (error) {
      console.error('Error counting active subscriptions:', error);
      return 0;
    }
  }

  // Get recent subscriptions for display
  async getRecentSubscriptions(
    merchantId: string,
    limitCount: number = 10
  ): Promise<Subscription[]> {
    try {
      const subscriptionsQuery = query(
        collectionGroup(db, 'subscriptions'),
        where('merchantId', '==', merchantId),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
      
      const snapshot = await getDocs(subscriptionsQuery);
      return snapshot.docs.map(doc => this.normalizeSubscription(doc.data(), doc.id));
    } catch (error) {
      console.error('Error fetching recent subscriptions:', error);
      return [];
    }
  }

  // Get subscription growth over time
  async getSubscriptionGrowth(
    merchantId: string,
    days: number = 30
  ): Promise<{ date: Date; count: number }[]> {
    const growth: { date: Date; count: number }[] = [];
    const now = new Date();
    
    for (let i = days; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      date.setHours(0, 0, 0, 0);
      
      const nextDate = new Date(date);
      nextDate.setDate(nextDate.getDate() + 1);
      
      try {
        const subscriptionsQuery = query(
          collectionGroup(db, 'subscriptions'),
          where('merchantId', '==', merchantId),
          where('status', '==', 'active'),
          where('createdAt', '<=', Timestamp.fromDate(nextDate))
        );
        
        const snapshot = await getCountFromServer(subscriptionsQuery);
        growth.push({ date, count: snapshot.data().count });
      } catch (error) {
        console.error(`Error fetching growth data for ${date}:`, error);
        growth.push({ date, count: 0 });
      }
    }
    
    return growth;
  }
}

export const subscriptionsService = new SubscriptionsService();
